/**
 * Page détail signalement — vue mairie (mais lisible aussi par l'administré créateur).
 *
 * Affiche :
 *   - En-tête : numéro + statut + urgence + catégorie + date
 *   - Description + localisation + photos
 *   - Créateur (nom, email, téléphone)
 *   - Actions de workflow (transition de statut)
 *   - Journal métier (événements)
 *
 * Refonte R6.1 (mai 2026) :
 *   - Topbar glass cohérent
 *   - Card principale glass avec en-tête numéro + badges + urgence
 *   - Sous-sections en eyebrow + bloc glass discret pour notifications
 *   - Actions de workflow en Card glass séparée
 *   - Journal métier avec dots tech-coloré sur ring bg
 *
 * Logique métier 100% préservée :
 *   - get + evenements + agents (canAssign), reload pattern
 *   - canModify (super_admin/admin/manager/agent), canAssign (super_admin/admin/manager)
 *   - NEXT_STATUTS transitions map ; historique humanisé via EventTimeline (L13.16.1)
 *   - transitionTo (avec commentaire optionnel + agent picker)
 *   - assignToAgent (sans changement statut)
 *   - Phase 2A L14 : bouton "Générer arrêté" si voirie + canModify
 *   - Message informatif pour administré
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { FicheDevisSection } from '@/components/service-technique/FicheDevisSection';
import { FicheSaisonSection } from '@/components/service-technique/FicheSaisonSection';
import { FaireSignerButton } from '@/components/signature/FaireSignerButton';
import { useToast } from '@/components/ui/Toast';
import { EventTimeline } from '@/components/ui/EventTimeline';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import {
  ArrowLeft, Hammer, Landmark, AlertTriangle, CircleAlert, Circle,
  MapPin, Mail, Phone, User, MessageSquare, Clock, Tag, AlertCircle,
  FileText, ImageIcon, CheckCircle2, CalendarClock, Inbox, Archive, Wrench, Loader2, RotateCcw, Receipt, ClipboardList,
} from 'lucide-react';
import type {
  Signalement, SignalementEvenement, SignalementStatut, SignalementUrgence,
  SignalementPhoto, ChecklistItem as ChecklistItemShared,
} from '@roronoa-collectivites/shared-types';
import { statutBadge } from '@/pages/service-technique/statut-badge';
import {
  SIGNALEMENT_STATUTS, SIGNALEMENT_CATEGORIES, SIGNALEMENT_SOURCES,
} from '@roronoa-collectivites/shared-types';
import { ChecklistEditor } from '@/pages/service-technique/ChecklistEditor';
import { ImageUploader } from '@/components/ImageUploader';
import { AssignEquipeModal } from '@/pages/service-technique/AssignEquipeModal';
import { DevisModal } from '@/pages/service-technique/DevisModal';
import { rafraichirNotifications } from '@/components/NotificationBell';

const URGENCE_ICON: Record<SignalementUrgence, typeof AlertTriangle> = {
  urgent: AlertTriangle,
  rapide: CircleAlert,
  normal: Circle,
};

const URGENCE_COLOR: Record<SignalementUrgence, string> = {
  urgent: 'text-urgent',
  rapide: 'text-warning',
  normal: 'text-ink-subtle',
};

// Transitions autorisées (doivent matcher backend STATUT_TRANSITIONS)
const NEXT_STATUTS: Record<SignalementStatut, SignalementStatut[]> = {
  recu: ['planifie', 'en_cours', 'archive'],
  planifie: ['en_cours', 'recu', 'archive'],
  en_cours: ['resolu', 'planifie', 'archive'],
  resolu: ['archive', 'en_cours'],
  archive: [],
};

// Topbar shared style (cohérent DashboardPage R4)
const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

export function SignalementDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, tenant } = useAuth();
  const { toast } = useToast();

  const [signalement, setSignalement] = useState<Signalement | null>(null);
  // L13.16.7 — id du créneau de planning encore actif lié (null si aucun).
  // Renvoyé par l'API de détail. Conditionne le bouton « Partiellement terminé ».
  const [creneauActifId, setCreneauActifId] = useState<string | null>(null);
  const [evenements, setEvenements] = useState<SignalementEvenement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // L13.15 — le retour d'action passe désormais par le toast global
  // (plus de actionError/actionSuccess en Alert qui décalait le contenu).
  const [actionLoading, setActionLoading] = useState(false);
  const [planifOpen, setPlanifOpen] = useState(false);
  const [devisOpen, setDevisOpen] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItemShared[]>([]);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [commentaire, setCommentaire] = useState('');

  // L13.8 — photos (chargées séparément du signalement pour qu'un upload
  // n'oblige pas à recharger toute la fiche). Restauré L13.14 (perdu en L13.11).
  const [photos, setPhotos] = useState<SignalementPhoto[]>([]);

  // Phase 2A core L10 — picker assignation
  const [agents, setAgents] = useState<Array<{ id: string; fullName: string; email: string; role: string }>>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');

  const isAdministre = user?.role === 'administre';
  const isAgent = user?.role === 'agent';  // L13.11
  const canModify = user && ['super_admin', 'admin', 'manager', 'agent'].includes(user.role);
  const canAssign = user && ['super_admin', 'admin', 'manager'].includes(user.role);
  // L13.11 — l'agent ne peut ni archiver, ni changer le statut "à plat" (seules les
  // transitions planifie→en_cours et en_cours→resolu sont permises par le backend).
  // Pour le bouton "Générer arrêté", c'est manager+ uniquement.
  const canArchive = user && ['super_admin', 'admin', 'manager'].includes(user.role);
  const canGenererArrete = canAssign; // même cohorte (manager+)

  async function reload() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const s = await api.serviceTechnique.signalements.get(id);
      setSignalement(s);
      setChecklist(s.checklist ?? []);
      // L13.16.7 — le champ creneauActifId voyage avec le détail (le type
      // partagé Signalement ne le déclare pas encore → lecture défensive).
      setCreneauActifId((s as { creneauActifId?: string | null }).creneauActifId ?? null);
      setSelectedAgentId(s.assigneUserId ?? '');
      // L13.16.2 — l'historique est désormais chargé aussi pour l'administré
      // (sa vue de suivi affiche la timeline). Le backend contrôle l'ownership.
      if (canModify || isAdministre) {
        try {
          const evts = await api.serviceTechnique.signalements.evenements(id);
          setEvenements(evts);
        } catch {
          // Non bloquant : la fiche reste consultable sans l'historique.
          setEvenements([]);
        }
      }
      // Charge la liste des agents pour le picker (admin/manager only)
      if (canAssign && agents.length === 0) {
        try {
          const ag = await api.serviceTechnique.signalements.agents();
          setAgents(ag.items);
        } catch {
          /* picker reste vide si pas autorisé — pas bloquant */
        }
      }
      // L13.8 — charge la liste des photos (silencieux si stockage non configuré).
      // Restauré L13.14.
      try {
        const photoList = await api.serviceTechnique.signalements.listPhotos(id);
        setPhotos(photoList.items);
      } catch {
        setPhotos([]);
      }
    } catch (err) {
      if (err instanceof ApiException) setError(err.payload.error);
      else setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  // L13.8 — rechargement isolé des photos (après upload/delete pour éviter
  // de re-fetch tout le signalement). Restauré L13.14.
  async function reloadPhotos() {
    if (!id) return;
    try {
      const photoList = await api.serviceTechnique.signalements.listPhotos(id);
      setPhotos(photoList.items);
    } catch {
      // Silencieux
    }
  }

  // Mig 215 — enregistrement de la checklist de l'état des lieux.
  async function saveChecklist() {
    if (!id) return;
    setChecklistSaving(true);
    setError(null);
    try {
      const updated = await api.serviceTechnique.signalements.updateChecklist(id, checklist);
      setSignalement(updated);
      setChecklist(updated.checklist ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setChecklistSaving(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function transitionTo(newStatut: SignalementStatut) {
    if (!id) return;
    setActionLoading(true);
    try {
      await api.serviceTechnique.signalements.updateStatut(id, {
        statut: newStatut,
        commentaire: commentaire.trim() || undefined,
        // Si un agent a été sélectionné dans le picker, on l'assigne en même temps
        assigneUserId: selectedAgentId || undefined,
      }); rafraichirNotifications();
      toast.success(
        `Statut mis à jour vers « ${SIGNALEMENT_STATUTS.find((s) => s.value === newStatut)?.label} »`,
      );
      setCommentaire('');
      await reload();
    } catch (err) {
      if (err instanceof ApiException) toast.error(err.payload.error);
      else toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  }

  /** Assignation seule (sans changement de statut). Utilise la même API. */
  async function assignToAgent() {
    if (!id || !signalement) return;
    setActionLoading(true);
    try {
      await api.serviceTechnique.signalements.updateStatut(id, {
        statut: signalement.statut,
        assigneUserId: selectedAgentId || undefined,
      }); rafraichirNotifications();
      const agent = agents.find((a) => a.id === selectedAgentId);
      toast.success(
        selectedAgentId
          ? `Affecté à ${agent?.fullName ?? 'l\'agent sélectionné'}.`
          : 'Affectation supprimée.',
      );
      await reload();
    } catch (err) {
      if (err instanceof ApiException) toast.error(err.payload.error);
      else toast.error('Erreur lors de l\'assignation');
    } finally {
      setActionLoading(false);
    }
  }

  /**
   * L13.16.7 — « Partiellement terminé ».
   * L'agent (ou le manager) est intervenu mais le chantier n'est pas fini :
   * on clôt le créneau du jour en 'partiellement_termine'. Le backend
   * (updateStatutCreneau, L13.16.4) ramène alors le signalement à 'planifie'
   * pour qu'il puisse être replanifié (nouveau créneau « Suite »).
   */
  async function markPartiel() {
    if (!id || !creneauActifId) return;
    setActionLoading(true);
    try {
      await api.serviceTechnique.planning.updateStatutCreneau(creneauActifId, {
        statut: 'partiellement_termine',
        commentaire: commentaire.trim() || undefined,
      });
      toast.success('Intervention partielle enregistrée — le signalement reste à replanifier.');
      setCommentaire('');
      await reload();
    } catch (err) {
      if (err instanceof ApiException) toast.error(err.payload.error);
      else toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setActionLoading(false);
    }
  }

  // R13.1 — Topbar partagé (identique aux autres pages service-technique)
  const Topbar = (
    <AppHeader module="Service technique" accent="tech" />
  );

  // R13.1 — Sub-nav (la pièce manquante d'origine pour la fiche détail).
  // Affiché uniquement pour les rôles pro. L'administré arrive via /mes-signalements
  // donc on lui montre juste un bouton retour neutre dans le contenu.
  const SubNav = !isAdministre ? (
    <nav
      aria-label="Navigation Service technique"
      className="border-b border-border backdrop-blur-glass px-4 py-2"
      style={{ backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))' }}
    >
      <div className="mx-auto max-w-3xl">
        <ServiceTechniqueNav
          active="incidents"
          right={
            <Button variant="ghost" size="sm" onClick={() => navigate('/service-technique/signalements')}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Liste</span>
            </Button>
          }
        />
      </div>
    </nav>
  ) : null;

  // ===== Loading =====
  if (loading) {
    return (
      <div className="min-h-screen text-ink">
        {Topbar}
        {SubNav}
        <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 animate-fade-in space-y-4">
          <SkeletonCard lines={5} label="Chargement du signalement" />
          <SkeletonCard lines={3} withTitle={false} />
        </main>
      </div>
    );
  }

  // ===== Erreur ou introuvable =====
  if (error || !signalement) {
    return (
      <div className="min-h-screen text-ink">
        {Topbar}
        {SubNav}
        <main className="mx-auto max-w-3xl px-4 py-8 animate-fade-in space-y-4">
          <Alert variant="error" icon={AlertCircle}>{error ?? 'Signalement introuvable'}</Alert>
          <Button variant="secondary" size="sm" onClick={() => navigate(isAdministre ? '/mes-signalements' : '/service-technique/signalements')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour
          </Button>
        </main>
      </div>
    );
  }

  const s = signalement;
  const UrgenceIcon = URGENCE_ICON[s.urgence];
  const badge = statutBadge(s);
  const categorieLabel = SIGNALEMENT_CATEGORIES.find((x) => x.value === s.categorie)?.label ?? s.categorie;

  // ==========================================================================
  // L13.16.2 — Vue administré « ma fiche de suivi »
  // (conforme mockup_admin_suivi_incident_et_notif — écran A).
  // Return dédié : ne touche pas au rendu pro ci-dessous.
  // ==========================================================================
  if (isAdministre) {
    return (
      <div className="min-h-screen text-ink">
        {Topbar}
        <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 animate-fade-in space-y-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/mes-signalements')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Mes signalements
          </Button>

          {/* En-tête fiche */}
          <div className="flex items-center gap-3">
            <LogoMark icon={Hammer} accent="tech" size="md" />
            <div className="min-w-0">
              <div className="font-mono text-xs text-ink-muted">{s.numero}</div>
              <h1 className="font-display text-lg font-semibold tracking-r-snug text-ink truncate">
                {s.sujet}
              </h1>
            </div>
          </div>

          {/* Status card */}
          <SuiviStatusCard signalement={s} />

          {/* Ref card : déclaré le / délai / catégorie */}
          <Card variant="glass" padded>
            <dl className="space-y-2.5 text-sm">
              <RefRow label="Déclaré le" value={formatDateLong(s.createdAt)} />
              <RefRow
                label="Délai de traitement"
                value={
                  s.resoluAt
                    ? formatDuration(new Date(s.resoluAt).getTime() - new Date(s.createdAt).getTime())
                    : 'en cours'
                }
                accent={!!s.resoluAt}
              />
              <RefRow label="Catégorie" value={`${categorieLabel} · ${s.urgence}`} />
            </dl>
          </Card>

          {/* Ce que vous avez signalé */}
          <Card variant="glass" padded>
            <SectionEyebrow as="h2">Ce que vous avez signalé</SectionEyebrow>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink">
              {s.description}
            </p>
            {(s.localisationAdresse || s.localisationQuartier) && (
              <p className="mt-3 flex items-center gap-2 text-sm text-ink-muted">
                <MapPin className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                {s.localisationAdresse}
                {s.localisationQuartier && <span> · {s.localisationQuartier}</span>}
              </p>
            )}
          </Card>

          {/* État des lieux (mig 215) — checklist des points inspectés */}
          {s.estEtatDesLieux && (
            <Card variant="glass" padded>
              <div className="flex items-center justify-between gap-2">
                <SectionEyebrow as="h2" className="flex items-center gap-2">
                  <ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
                  État des lieux — points inspectés
                </SectionEyebrow>
                {canModify && (
                  <Button variant="secondary" size="sm" loading={checklistSaving} onClick={() => void saveChecklist()}>
                    Enregistrer
                  </Button>
                )}
              </div>
              <div className="mt-3">
                <ChecklistEditor items={checklist} onChange={setChecklist} readOnly={!canModify} />
              </div>
              {checklist.some((c) => c.etat === 'degrade') && canAssign && (
                <p className="mt-3 text-xs text-ink-subtle">
                  Un point est dégradé : utilisez « Demander un devis » plus haut pour chiffrer la remise en état.
                </p>
              )}
            </Card>
          )}

          {/* Photos avant / après (lecture seule) */}
          {photos.some((p) => p.kind === 'agent_avant' || p.kind === 'agent_apres') && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="flex items-center gap-2">
                <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Photos avant / après
              </SectionEyebrow>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <PhotoSlot
                  signalementId={s.id}
                  photo={photos.find((p) => p.kind === 'agent_avant') ?? null}
                  label="avant"
                />
                <PhotoSlot
                  signalementId={s.id}
                  photo={photos.find((p) => p.kind === 'agent_apres') ?? null}
                  label="après"
                  done
                />
              </div>
            </Card>
          )}

          {/* Suivi de l'intervention — timeline */}
          {evenements.length > 0 && (
            <Card variant="glass" padded>
              <SectionEyebrow as="h2" className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                Suivi de l'intervention
              </SectionEyebrow>
              <EventTimeline className="mt-4" events={evenements} />
            </Card>
          )}

          {/* Remerciement */}
          {(s.statut === 'resolu' || s.statut === 'archive') && (
            <Card variant="glass" padded className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-success/30 bg-success-tint/40 text-success">
                <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
              </div>
              <p className="mt-3 font-display text-base font-semibold tracking-r-snug text-ink">
                Merci pour votre signalement&nbsp;!
              </p>
              <p className="mt-1 text-sm text-ink-muted">
                Votre vigilance aide la commune à rester sûre et agréable pour tout le monde.
              </p>
            </Card>
          )}
        </main>
      </div>
    );
  }

  // L13.11 — restriction des transitions pour l'agent : il ne peut faire que
  // planifie→en_cours (démarrer) et en_cours→resolu (terminer). Les autres
  // transitions (archive notamment) sont cachées de l'UI. Le backend rejette
  // les transitions interdites en plus (defense in depth).
  const AGENT_ALLOWED: Record<string, string[]> = {
    planifie: ['en_cours'],
    en_cours: ['resolu'],
  };
  const allowedNextRaw = NEXT_STATUTS[s.statut];
  const allowedNext = isAgent
    ? allowedNextRaw.filter((next) => (AGENT_ALLOWED[s.statut] ?? []).includes(next))
    : allowedNextRaw;

  // L13.16.6 — clôturer un signalement (→ résolu) exige une photo « après
  // intervention ». L'agent comme le manager doivent documenter le chantier
  // avant de clôturer (« marquer terminé = résolu », avec preuve).
  const hasPhotoApres = photos.some((p) => p.kind === 'agent_apres');

  // R13.1 — Return principal : topbar + sub-nav (uniquement pour rôles pro) + contenu
  return (
    <div className="min-h-screen text-ink">
      {Topbar}
      {SubNav}
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 animate-fade-in space-y-4">
        {isAdministre && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/mes-signalements')}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Mes signalements
          </Button>
        )}

        {/* ===== Card principale ===== */}
        <Card variant="glass" padded>
          {/* En-tête : LogoMark + numéro + badges + urgence */}
          <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <LogoMark icon={Hammer} accent="tech" size="md" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono text-ink-muted">{s.numero}</span>
                  <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                  <Badge variant="tech" size="sm">
                    <Tag className="h-3 w-3" aria-hidden="true" />
                    {categorieLabel}
                  </Badge>
                  {/* 3.1 — origine de la fiche : distingue un constat du service
                      d'un signalement déposé par un habitant. */}
                  {s.source && s.source !== 'web' && (
                    <Badge variant="neutral" size="sm">
                      {SIGNALEMENT_SOURCES.find((x) => x.value === s.source)?.label ?? s.source}
                    </Badge>
                  )}
                </div>
                <h1 className="mt-2 font-display text-lg font-semibold tracking-r-snug text-ink">
                  {s.sujet}
                </h1>
              </div>
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium flex-shrink-0 ${URGENCE_COLOR[s.urgence]}`}>
              <UrgenceIcon className="h-4 w-4" aria-hidden="true" />
              <span className="capitalize">{s.urgence}</span>
            </div>
          </div>

          <div className="pt-4 space-y-5">
            {/* Description */}
            <div>
              <SectionEyebrow as="h2">Description</SectionEyebrow>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink">{s.description}</p>
            </div>

            {/* Localisation */}
            {(s.localisationAdresse || s.localisationQuartier) && (
              <div>
                <SectionEyebrow as="h2">Localisation</SectionEyebrow>
                <p className="mt-2 flex items-center gap-2 text-sm text-ink">
                  <MapPin className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
                  {s.localisationAdresse}
                  {s.localisationQuartier && <span className="text-ink-muted"> · {s.localisationQuartier}</span>}
                </p>
              </div>
            )}

            {/* L13.8 — Galerie photos en 3 sections (initial / avant / après).
                Restauré L13.14 (perdu lors de la refonte RBAC L13.11). */}
            <div>
              <SectionEyebrow as="h2" className="flex items-center gap-2">
                <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Photos
              </SectionEyebrow>

              <div className="mt-2 space-y-4">
                {/* Photo initiale — preuve de l'administré. L13.16.4.1 : VERROUILLÉE
                    en lecture seule pour les pros (ni upload, ni suppression). Les
                    services techniques ne touchent jamais à la photo du citoyen ;
                    leur travail passe par les photos avant / après. La photo
                    initiale est désormais fournie à la déclaration (L13.16.3). */}
                <ImageUploader
                  signalementId={s.id}
                  kind="admin_initial"
                  existingPhotos={photos}
                  label="Photo initiale (création)"
                  canUpload={false}
                  canDelete={false}
                  onUploadSuccess={reloadPhotos}
                  onDeleteSuccess={reloadPhotos}
                />

                {/* Avant intervention (agent uniquement) */}
                {canModify && (
                  <ImageUploader
                    signalementId={s.id}
                    kind="agent_avant"
                    existingPhotos={photos}
                    label="Avant intervention"
                    canUpload={canModify && (s.statut === 'en_cours' || s.statut === 'planifie')}
                    canDelete={canModify}
                    onUploadSuccess={reloadPhotos}
                    onDeleteSuccess={reloadPhotos}
                  />
                )}

                {/* Après intervention (agent uniquement, et seulement si en cours ou résolu) */}
                {canModify && (
                  <ImageUploader
                    signalementId={s.id}
                    kind="agent_apres"
                    existingPhotos={photos}
                    label="Après intervention"
                    canUpload={canModify && (s.statut === 'en_cours' || s.statut === 'resolu')}
                    canDelete={canModify}
                    onUploadSuccess={reloadPhotos}
                    onDeleteSuccess={reloadPhotos}
                  />
                )}
              </div>
            </div>

            {/* Créateur (visible pour agents+) */}
            {canModify && (
              <div>
                <SectionEyebrow as="h2">Créateur</SectionEyebrow>
                <div className="mt-2 grid grid-cols-1 gap-1.5 text-sm sm:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
                    <span>{s.createurNom ?? '—'}</span>
                  </div>
                  {s.createurEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
                      <a href={`mailto:${s.createurEmail}`} className="text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md">
                        {s.createurEmail}
                      </a>
                    </div>
                  )}
                  {s.createurTelephone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
                      <a href={`tel:${s.createurTelephone}`} className="text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md">
                        {s.createurTelephone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notifications administré — bloc glass discret */}
            <div className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass p-3 text-xs">
              <div className="font-medium text-ink">Notifications administré</div>
              <ul className="mt-1.5 space-y-0.5 text-ink-muted">
                <li className="flex items-center gap-2">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.notifRecuEnvoyeeAt ? 'bg-success' : 'bg-ink-dim'}`} aria-hidden="true" />
                  Prise en compte : {s.notifRecuEnvoyeeAt ? new Date(s.notifRecuEnvoyeeAt).toLocaleString('fr-FR') : 'non envoyée'}
                </li>
                <li className="flex items-center gap-2">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.notifResoluEnvoyeeAt ? 'bg-success' : 'bg-ink-dim'}`} aria-hidden="true" />
                  Résolution : {s.notifResoluEnvoyeeAt ? new Date(s.notifResoluEnvoyeeAt).toLocaleString('fr-FR') : 'en attente'}
                </li>
              </ul>
            </div>
          </div>
        </Card>

        {/* ===== Actions de workflow (mairie uniquement) ===== */}
        {canModify && allowedNext.length > 0 && (
          <Card variant="glass" padded>
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              Actions de traitement
            </h2>

            <div className="mt-4 space-y-3">
              {/* Phase 2A core L10 — Picker d'assignation agent */}
              {canAssign && (
                <Field
                  label="Assigner à un agent / manager"
                  htmlFor="assigneUserId"
                  hint={
                    signalement!.assigneUserId
                      ? `Actuellement assigné. Changer ci-dessous puis "Mettre à jour".`
                      : 'Aucun agent assigné. Sélectionnez quelqu\'un puis cliquez sur "Mettre à jour".'
                  }
                >
                  <div className="flex gap-2">
                    <select
                      id="assigneUserId"
                      value={selectedAgentId}
                      onChange={(e) => setSelectedAgentId(e.target.value)}
                      className="flex-1 h-10 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                    >
                      <option value="">— Non assigné —</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.fullName} ({a.role === 'manager' ? 'Manager' : 'Agent'}) · {a.email}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={assignToAgent}
                      disabled={actionLoading || selectedAgentId === (signalement!.assigneUserId ?? '')}
                    >
                      Mettre à jour
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPlanifOpen(true)}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-r-md border border-tech/50 bg-tech-tint/40 px-3 py-1.5 text-xs font-semibold text-tech-fg transition hover:bg-tech-tint/70"
                  >
                    📅 Planifier sur le planning (équipe / horaire)
                  </button>
                </Field>
              )}

              <Field
                label="Commentaire (optionnel)"
                htmlFor="commentaire"
                hint="Visible dans le journal métier et utilisé pour la notification administré"
              >
                <textarea
                  id="commentaire"
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Ex : Affecté à l'équipe Marc et Pierre. Intervention prévue jeudi 14h."
                  className="w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                />
              </Field>

              <div className="flex flex-wrap gap-2 pt-1">
                {allowedNext.map((next) => {
                  // L13.16.6 — clôture (→ résolu) bloquée tant qu'il n'y a pas
                  // de photo « après intervention ».
                  const resoluBloque = next === 'resolu' && !hasPhotoApres;
                  return (
                    <Button
                      key={next}
                      variant={next === 'resolu' ? 'success' : next === 'archive' ? 'secondary' : 'primary'}
                      size="sm"
                      onClick={() => transitionTo(next)}
                      loading={actionLoading}
                      disabled={actionLoading || resoluBloque}
                      title={resoluBloque ? 'Ajoutez une photo « après intervention » avant de clôturer' : undefined}
                    >
                      → {SIGNALEMENT_STATUTS.find((x) => x.value === next)?.label}
                    </Button>
                  );
                })}
                {/* L13.16.7 — Partiellement terminé : clôt le créneau actif sans résoudre.
                    Masqué pour un état des lieux (cycle allégé : planifié → en cours → résolu). */}
                {canModify && creneauActifId && !s.estEtatDesLieux && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={markPartiel}
                    loading={actionLoading}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    Partiellement terminé
                  </Button>
                )}
                {/* L13.11 — Générer arrêté : manager+ uniquement (pas agent) */}
                {signalement!.categorie === 'voirie' && canGenererArrete && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(`/service-technique/arretes/nouveau?signalementIds=${signalement!.id}`)}
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    Générer un arrêté municipal
                  </Button>
                )}
                {/* Devis (V1, mig 214) : demander un devis à un prestataire — manager+ */}
                {canAssign && (
                  <Button variant="secondary" size="sm" onClick={() => setDevisOpen(true)}>
                    <Receipt className="h-4 w-4" aria-hidden="true" />
                    Demander un devis
                  </Button>
                )}
              </div>

              {/* L13.16.6 — rappel quand la clôture est bloquée faute de photo après */}
              {allowedNext.includes('resolu') && !hasPhotoApres && (
                <p className="text-2xs text-warning">
                  Pour clôturer ce signalement, ajoutez d'abord une photo «&nbsp;après
                  intervention&nbsp;» dans la galerie photos ci-dessus.
                </p>
              )}

              {/* L13.16.7 — explication du « Partiellement terminé » */}
              {canModify && creneauActifId && (
                <p className="text-2xs text-ink-subtle">
                  «&nbsp;Partiellement terminé&nbsp;» clôt le créneau d'intervention en
                  cours sans résoudre le signalement : il pourra être replanifié.
                </p>
              )}
            </div>
          </Card>
        )}

        {/* ===== Vague 4c — le parcours devis (3.13), à côté du statut, jamais dessus ===== */}
        {canModify && id && <FicheDevisSection signalementId={id} />}
        {/* M1 (retour Manon) — un état des lieux se signe (signature simple). */}
        {canModify && id && signalement?.estEtatDesLieux && (
          <section className="mt-4 rounded-r-xl border border-border/60 p-4">
            <h2 className="mb-1 text-sm font-semibold text-ink">Signature de l'état des lieux</h2>
            <FaireSignerButton documentType="etat_des_lieux" documentId={id} />
          </section>
        )}
        {/* M12 (retour Manon) — saisonnalité : les délais comptent à partir de l'ouverture. */}
        {canModify && id && signalement && (
          <FicheSaisonSection signalementId={id} saisonOuverture={signalement.saisonOuverture}
            saisonMotif={signalement.saisonMotif} onSaved={() => void reload()} />
        )}

        {/* ===== Journal métier ===== */}
        {canModify && evenements.length > 0 && (
          <Card variant="glass" padded>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
              <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
                Historique
              </h2>
            </div>
            {/* L13.16.1 — timeline humanisée (conforme mockup suivi_incident) */}
            <EventTimeline
              className="mt-4"
              events={evenements}
              resolveUserName={(uid) => agents.find((a) => a.id === uid)?.fullName}
            />
          </Card>
        )}

        {/* ===== Message informatif pour administré ===== */}
        {isAdministre && (
          <Card variant="glass" padded>
            <div className="flex items-start gap-2 text-sm text-ink-muted">
              <MessageSquare className="h-4 w-4 shrink-0 mt-0.5 text-ink-subtle" aria-hidden="true" />
              <div>
                Vous recevrez un email automatique dès que les équipes de la mairie prendront en charge
                votre signalement, puis à nouveau lorsqu'il sera résolu.
              </div>
            </div>
          </Card>
        )}

        {planifOpen && signalement && (
          <AssignEquipeModal
            signalement={{ id: signalement.id, numero: signalement.numero, sujet: signalement.sujet }}
            date={new Date()}
            defaultHour={8}
            pickDate
            onClose={() => setPlanifOpen(false)}
            onDone={() => { setPlanifOpen(false); void reload(); }}
          />
        )}
        {devisOpen && signalement && (
          <DevisModal
            signalementId={signalement.id}
            signalementNumero={signalement.numero}
            onClose={() => setDevisOpen(false)}
            onDone={() => { setDevisOpen(false); void reload(); }}
          />
        )}
      </main>
    </div>
  );
}

// ============================================================================
// L13.16.2 — Helpers + sous-composants de la vue administré (suivi de fiche)
// ============================================================================

const MOIS_COURT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** « 14 mai · 8h12 » */
function formatDateLong(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${MOIS_COURT[d.getMonth()]} · ${h}h${m}`;
}

/** Durée lisible : « 12 min », « 5 h 47 », « 2 j 3 h ». */
function formatDuration(ms: number): string {
  if (ms < 0) ms = 0;
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const restMin = min % 60;
  if (h < 24) return restMin > 0 ? `${h} h ${String(restMin).padStart(2, '0')}` : `${h} h`;
  const j = Math.floor(h / 24);
  const restH = h % 24;
  return restH > 0 ? `${j} j ${restH} h` : `${j} j`;
}

/** Temps relatif passé : « à l'instant », « il y a 32 min », « il y a 3 h », « il y a 2 j ». */
function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  return `il y a ${j} j`;
}

// Apparence de la status card selon le statut courant.
const SUIVI_STATUT_META: Record<
  SignalementStatut,
  { icon: typeof CheckCircle2; tone: string; pastille: string; label: string; sub: string }
> = {
  recu: {
    icon: Inbox, tone: 'text-info', pastille: 'border-info/30 bg-info-tint/40',
    label: 'Signalement bien reçu', sub: 'En attente d\'affectation à un agent',
  },
  planifie: {
    icon: CalendarClock, tone: 'text-info', pastille: 'border-info/30 bg-info-tint/40',
    label: 'Intervention planifiée', sub: 'Un agent va prendre en charge votre signalement',
  },
  en_cours: {
    icon: Wrench, tone: 'text-warning', pastille: 'border-warning/30 bg-warning-tint/40',
    label: 'Intervention en cours', sub: 'Les équipes techniques sont sur le terrain',
  },
  resolu: {
    icon: CheckCircle2, tone: 'text-success', pastille: 'border-success/30 bg-success-tint/40',
    label: 'Résolu', sub: 'L\'intervention est terminée',
  },
  archive: {
    icon: Archive, tone: 'text-ink-muted', pastille: 'border-border bg-surface-hi',
    label: 'Fiche clôturée', sub: 'Ce signalement est archivé',
  },
};

/** Carte de statut en tête de la vue de suivi administré. */
function SuiviStatusCard({ signalement }: { signalement: Signalement }) {
  const meta = SUIVI_STATUT_META[signalement.statut];
  const Icon = meta.icon;
  // Pour un signalement résolu, on précise « il y a … » à partir de resoluAt.
  const sub =
    signalement.statut === 'resolu' && signalement.resoluAt
      ? `${meta.sub} · ${formatRelative(signalement.resoluAt)}`
      : meta.sub;

  return (
    <Card variant="glass" padded>
      <div className="flex items-center gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-r-xl border ${meta.pastille} ${meta.tone}`}
          aria-hidden="true"
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <div className={`font-display text-base font-semibold tracking-r-snug ${meta.tone}`}>
            {meta.label}
          </div>
          <div className="mt-0.5 text-sm text-ink-muted">{sub}</div>
        </div>
      </div>
    </Card>
  );
}

/** Ligne d'une « ref card » (libellé à gauche, valeur à droite). */
function RefRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-subtle">{label}</dt>
      <dd className={`font-medium tabular-nums ${accent ? 'text-success' : 'text-ink'}`}>{value}</dd>
    </div>
  );
}

/**
 * Vignette photo en lecture seule (vue administré). Charge la signed URL au
 * montage ; si aucune photo ou stockage indisponible, affiche un emplacement neutre.
 */
function PhotoSlot({
  signalementId,
  photo,
  label,
  done,
}: {
  signalementId: string;
  photo: SignalementPhoto | null;
  label: string;
  done?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!photo) {
      setUrl(null);
      return;
    }
    (async () => {
      try {
        const r = await api.serviceTechnique.signalements.getPhotoSignedUrl(signalementId, photo.id);
        if (!cancelled) setUrl(r.url);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signalementId, photo]);

  return (
    <figure className="m-0">
      <div
        className={`relative aspect-square overflow-hidden rounded-r-lg border ${
          done ? 'border-success/30' : 'border-border'
        } bg-surface/40`}
      >
        <span
          className={`absolute left-1.5 top-1.5 z-10 rounded-r-pill border px-2 py-0.5 text-2xs font-medium backdrop-blur-glass ${
            done
              ? 'border-success/35 bg-success-tint/70 text-success'
              : 'border-border bg-surface/80 text-ink-muted'
          }`}
        >
          {label}
        </span>
        {photo && url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
            <img src={url} alt={`Photo ${label}`} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
          </a>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-ink-dim">
            {photo ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <ImageIcon className="h-6 w-6" aria-hidden="true" />
            )}
          </div>
        )}
      </div>
      {!photo && (
        <figcaption className="mt-1 text-center text-2xs text-ink-subtle">
          Pas encore de photo
        </figcaption>
      )}
    </figure>
  );
}
